import type {
  FuturesOpenPosition,
  FuturesPositionHistory,
  PaginatedItems,
  ProfileInfoResponse,
  ProfileOpenPosition,
  ProfilePositionHistory,
} from './profile-types';

interface RawPaginatedItems {
  i: unknown[];
  p: number;
  s: number;
  tp: number;
  ti?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapOpenPosition(dto: any): ProfileOpenPosition {
  return {
    eventId: dto.ei,
    eventOutcomeId: dto.eoi,
    assetId: dto.ai,
    eventTitle: dto.et,
    eventIcon: dto.eim,
    eventSlug: dto.es,
    outcomeName: dto.on,
    tokenName: dto.tn,
    sharesCount: dto.sc ?? 0,
    sharePrice: dto.sp ?? 0,
    side: dto.si,
    value: dto.v ?? 0,
    cost: dto.c ?? 0,
    pnlValue: dto.pv ?? 0,
    pnlPercent: dto.pp ?? 0,
    outcomeStatus: dto.ost,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPositionHistory(dto: any): ProfilePositionHistory {
  return {
    eventId: dto.ei,
    eventOutcomeId: dto.eoi,
    assetId: dto.ai,
    eventTitle: dto.et,
    eventIcon: dto.eim,
    eventSlug: dto.es,
    outcomeName: dto.on,
    tokenName: dto.tn,
    sharesCount: dto.sc ?? 0,
    sharePrice: dto.sp ?? 0,
    side: dto.si,
    value: dto.v ?? 0,
    cost: dto.c ?? 0,
    pnlValue: dto.pv ?? 0,
    pnlPercent: dto.pp ?? 0,
    openDate: dto.od,
    closeDate: dto.cd,
  };
}

function mapPaginatedItems<T>(
  raw: RawPaginatedItems | null | undefined,
  itemMapper: (dto: unknown) => T,
): PaginatedItems<T> | null {
  if (!raw) return null;
  return {
    items: Array.isArray(raw.i) ? raw.i.map(itemMapper) : [],
    page: raw.p ?? 0,
    size: raw.s ?? 0,
    totalPages: raw.tp ?? 0,
    totalItems: raw.ti,
  };
}

export function mapProfileInfoResponse(raw: Record<string, unknown>): ProfileInfoResponse {
  return {
    profile: raw.profile as ProfileInfoResponse['profile'],
    cards: raw.cards as ProfileInfoResponse['cards'],
  };
}

export function mapOpenPositionsPage(
  raw: RawPaginatedItems,
): PaginatedItems<ProfileOpenPosition> | null {
  return mapPaginatedItems(raw, mapOpenPosition);
}

export function mapPositionHistoryPage(
  raw: RawPaginatedItems,
): PaginatedItems<ProfilePositionHistory> | null {
  return mapPaginatedItems(raw, mapPositionHistory);
}

// ── Futures (EXCHANGE) ───────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapFuturesOpenPosition(dto: any): FuturesOpenPosition {
  return {
    positionId: dto.id,
    pairId: dto.pi,
    symbol: dto.sy ?? '',
    side: dto.s,
    size: dto.sz ?? 0,
    entryPrice: dto.e ?? 0,
    leverage: dto.l ?? 0,
    initialMargin: dto.im ?? 0,
    liquidationPrice: dto.lq ?? null,
    unrealizedPnl: dto.upn ?? 0,
    marginMode: dto.mmd,
    realizedPnl: dto.rpn ?? 0,
    totalFee: dto.tf ?? 0,
    accruedFunding: dto.af ?? 0,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapFuturesPositionHistory(dto: any): FuturesPositionHistory {
  return {
    positionId: dto.id,
    pairId: dto.pi,
    symbol: dto.sy ?? '',
    side: dto.s,
    closedQty: dto.sz ?? 0,
    entryPrice: dto.e ?? 0,
    avgExitPrice: dto.x ?? 0,
    leverage: dto.l ?? 0,
    marginMode: dto.mmd,
    status: dto.st,
    realizedPnl: dto.rpn ?? 0,
    totalFee: dto.tf ?? 0,
    accruedFunding: dto.af ?? 0,
    openedAt: dto.oa ?? 0,
    closedAt: dto.ca ?? 0,
  };
}

export function mapFuturesOpenPositionsPage(
  raw: RawPaginatedItems,
): PaginatedItems<FuturesOpenPosition> | null {
  return mapPaginatedItems(raw, mapFuturesOpenPosition);
}

export function mapFuturesPositionHistoryPage(
  raw: RawPaginatedItems,
): PaginatedItems<FuturesPositionHistory> | null {
  return mapPaginatedItems(raw, mapFuturesPositionHistory);
}
